import { MIN_VISIBLE_TILES, RENDER_ZOOM, TILE_SIZE } from '@shefira/shared';

export interface Viewport {
  width: number;
  height: number;
  zoom: number;
}

/**
 * The largest whole zoom, up to `RENDER_ZOOM`, that keeps `MIN_VISIBLE_TILES`
 * tiles along the screen's short side (ADR 0043). A desktop stays at 3x; a
 * phone, whichever way it is held, drops to 2x; never below 1x.
 */
export function pickZoom(screenWidth: number, screenHeight: number): number {
  const shortSide = Math.min(screenWidth, screenHeight);
  const fitting = Math.floor(shortSide / (MIN_VISIBLE_TILES * TILE_SIZE));
  return Math.max(1, Math.min(RENDER_ZOOM, fitting));
}

/**
 * The game canvas is rendered at native resolution then scaled by an integer
 * zoom. The logical size is floored so that every logical pixel maps to
 * exactly `zoom` screen pixels — no fractional scaling, no blur.
 */
export function computeViewport(
  screenWidth: number,
  screenHeight: number,
  zoom: number = pickZoom(screenWidth, screenHeight),
): Viewport {
  return {
    width: Math.max(1, Math.floor(screenWidth / zoom)),
    height: Math.max(1, Math.floor(screenHeight / zoom)),
    zoom,
  };
}

export interface CameraBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The camera's bounds for a map of `mapWidth`×`mapHeight` pixels seen through
 * a `viewWidth`×`viewHeight` view. Phaser pins a map narrower than the view to
 * its left (or top) edge; along such an axis the bounds grow to the view's
 * size, the map in their middle, so a small map (a dungeon) sits centred.
 * `bottomInset` is the height the HUD covers at the bottom of the view (a
 * phone's spell bar and chat): the bounds reach that far below the map, so
 * its last rows (a cave's way out) can scroll above the HUD. Whole pixels,
 * for the pixel-perfect render.
 */
export function cameraBounds(
  mapWidth: number,
  mapHeight: number,
  viewWidth: number,
  viewHeight: number,
  bottomInset = 0,
): CameraBounds {
  const axis = (map: number, view: number, inset: number): { start: number; size: number } => {
    if (map + inset >= view) return { start: 0, size: map + inset };
    // Centred in the part of the view the HUD leaves clear.
    return { start: -Math.floor((view - inset - map) / 2), size: view };
  };
  const inset = Math.max(0, Math.ceil(bottomInset));
  const horizontal = axis(mapWidth, viewWidth, 0);
  const vertical = axis(mapHeight, viewHeight, inset);
  return { x: horizontal.start, y: vertical.start, width: horizontal.size, height: vertical.size };
}
